import type { Deal, DealStageStep } from "../services/dealsService";

// ===========================================================================
// Recorrido de un negocio por el pipeline — every stage the deal went through in
// HubSpot, oldest first, with the date it entered and how long it stayed.
// HubSpot records those dates per stage; the sync brings them as `stageHistory`.
// ===========================================================================

export interface JourneyStep extends DealStageStep {
  /** Whole days spent in the stage; null while the deal is still in it with no exit date. */
  days: number | null;
  /** The stage the deal is in right now. */
  current: boolean;
}

const DAY = 24 * 60 * 60 * 1000;

/**
 * The steps to draw. A stage's stay ends at its own exit date, else at the entry
 * of the next step, else it is the stage the deal is still in (measured up to
 * `now`).
 */
export function journeyOf(deal: Pick<Deal, "stageHistory" | "hubspotStage">, now = Date.now()): JourneyStep[] {
  const steps = [...(deal.stageHistory ?? [])].sort((a, b) => String(a.enteredAt).localeCompare(String(b.enteredAt)));
  return steps.map((step, i) => {
    const entered = Date.parse(step.enteredAt);
    const next = steps[i + 1];
    const current = !next && (!deal.hubspotStage || step.stageId === deal.hubspotStage);
    const end = step.exitedAt ? Date.parse(step.exitedAt) : next ? Date.parse(next.enteredAt) : current ? now : NaN;
    const days = Number.isFinite(entered) && Number.isFinite(end) ? Math.max(0, Math.round((end - entered) / DAY)) : null;
    return { ...step, days, current };
  });
}

const fmtDate = (iso?: string | null) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? new Date(t).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—";
};

const stay = (days: number | null, current: boolean) =>
  days === null ? "" : `${days === 0 ? "menos de un día" : days === 1 ? "1 día" : `${days} días`}${current ? " hasta hoy" : ""}`;

const DOT: Record<string, string> = { won: "bg-emerald-500", lost: "bg-red-500", open: "bg-primary-500" };

interface Props {
  deal: Deal;
  textClass: string;
  mutedClass: string;
  borderClass: string;
}

export function DealJourney({ deal, textClass, mutedClass, borderClass }: Props) {
  const steps = journeyOf(deal);
  if (!steps.length) {
    return (
      <p data-testid="deal-journey-empty" className={`text-sm ${mutedClass}`}>
        Este negocio todavía no tiene su recorrido. Lanza «Importación completa» en Configuración → HubSpot para traerlo.
      </p>
    );
  }
  return (
    <ol data-testid="deal-journey" className="space-y-0">
      {steps.map((step, i) => (
        <li key={`${step.stageId}-${step.enteredAt}`} className="flex gap-3">
          <div className="flex flex-col items-center">
            <span className={`mt-1.5 h-3 w-3 flex-shrink-0 rounded-full ${DOT[step.kind ?? "open"] ?? DOT.open} ${step.current ? "ring-4 ring-primary-500/25" : ""}`} />
            {i < steps.length - 1 && <span className={`w-px flex-1 border-l ${borderClass}`} />}
          </div>
          <div className="pb-4">
            <p className={`text-sm font-semibold ${textClass}`}>
              {step.label}
              {step.current && <span className="ml-2 rounded-full bg-primary-500/15 px-2 py-0.5 text-[10px] font-medium text-primary-500">Etapa actual</span>}
            </p>
            <p className={`text-xs ${mutedClass}`}>
              Entró el {fmtDate(step.enteredAt)}
              {stay(step.days, step.current) && ` · ${stay(step.days, step.current)}`}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}
